import { useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import type { CatalogueBambu, TeinteBambu } from "./contract";
import { ALERTE, BOUTON_SECONDAIRE, CARTE } from "./silhouettes";
import { bom, type LigneFilament } from "./silhouettes-filaments";
import {
  libelleDeLaCouleur,
  noteDeLaLigne,
  texteDeLAlerteAms,
} from "./silhouettes-serre-livres-textes";
import type { Colonne } from "./silhouettes-verdict";

/**
 * La carte « Filaments du jeu » du mode serre-livres (#337, spec section 3) : chaque couleur
 * trouvee dans les deux rendus ramenee a un vrai PLA Bambu, une liste pour la corriger, le filament
 * des Bookends et des Socles, l'alerte quand une figurine passe les 4 filaments d'un AMS, et la BOM
 * a copier dans le formulaire de MakerWorld.
 *
 * Une correction ne relance aucun calcul : la page garde ses choix, et les retient dans le
 * navigateur pour les rendus suivants (`silhouettes-filaments`).
 */

/** Ce que la carte sait du catalogue : il arrive, ou il ne repond pas, ou il est la. */
export type CatalogueVu =
  | { etat: "attente" }
  | { etat: "erreur"; reessayer: () => void }
  | { etat: "pret"; catalogue: CatalogueBambu };

const GAMMES = ["PLA Basic", "PLA Matte"] as const;

const SELECTEUR =
  "min-h-11 w-full rounded-md border border-input bg-card pr-2 pl-8 text-[13.5px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-9 md:text-sm";

/**
 * Les teintes du catalogue, groupees par gamme, les habituelles de Nicolas en tete de leur groupe
 * (dans l'ordre ou le catalogue les donne), puis les autres dans l'ordre du catalogue. Une teinte
 * choisie que le catalogue ne connait pas (il a change depuis) est ajoutee a la sienne : la liste
 * ne dirait sinon pas ce qui est choisi.
 */
function groupesDeTeintes(
  catalogue: CatalogueBambu,
  choisie: TeinteBambu,
): { gamme: (typeof GAMMES)[number]; teintes: TeinteBambu[] }[] {
  const toutes = catalogue.teintes.some((teinte) => teinte.cle === choisie.cle)
    ? catalogue.teintes
    : [...catalogue.teintes, choisie];
  return GAMMES.map((gamme) => {
    const deLaGamme = toutes.filter((teinte) => teinte.gamme === gamme);
    const habituelles = catalogue.habituelles.flatMap((cle) =>
      deLaGamme.filter((teinte) => teinte.cle === cle),
    );
    const autres = deLaGamme.filter((teinte) => !catalogue.habituelles.includes(teinte.cle));
    return { gamme, teintes: [...habituelles, ...autres] };
  }).filter((groupe) => groupe.teintes.length > 0);
}

function Pastille({ hexa }: { hexa: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-3.5 shrink-0 rounded border border-border"
      style={{ backgroundColor: hexa }}
    />
  );
}

/** La liste des teintes, avec la pastille de celle qui est choisie : une liste native, groupee par gamme. */
function SelecteurDeTeinte({
  etiquette,
  catalogue,
  valeur,
  changer,
  className,
}: {
  etiquette: string;
  catalogue: CatalogueBambu;
  valeur: TeinteBambu;
  changer: (cle: string) => void;
  className?: string;
}) {
  return (
    <span className={cn("relative block min-w-0", className)}>
      <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2">
        <Pastille hexa={valeur.hexa} />
      </span>
      <select
        aria-label={etiquette}
        className={SELECTEUR}
        onChange={(event) => changer(event.target.value)}
        value={valeur.cle}
      >
        {groupesDeTeintes(catalogue, valeur).map((groupe) => (
          <optgroup key={groupe.gamme} label={groupe.gamme}>
            {groupe.teintes.map((teinte) => (
              <option key={teinte.cle} value={teinte.cle}>
                {`${teinte.nom_fr} · ${teinte.gamme} ${teinte.nom_en}`}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </span>
  );
}

/**
 * Les colonnes d'une ligne : trouvee, figurine, fleche, PLA Bambu, note (un seul rang, a partir de
 * `xl` : a `lg` la carte n'a que 690 px, et la note n'aurait plus de place). Dessous, la liste du PLA
 * Bambu passe sous la couleur et sa figurine, pleine largeur, comme la maquette du telephone.
 */
const RANG =
  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 xl:grid-cols-[150px_100px_24px_minmax(0,400px)_minmax(0,1fr)]";

type Copie = "copie" | "selection" | null;

/** Copie la BOM ; sans presse-papiers, la selectionne pour un Ctrl+C. */
async function copier(texte: string, zone: HTMLElement | null): Promise<Copie> {
  try {
    await navigator.clipboard.writeText(texte);
    return "copie";
  } catch {
    const selection = window.getSelection();
    if (zone && selection) {
      const plage = document.createRange();
      plage.selectNodeContents(zone);
      selection.removeAllRanges();
      selection.addRange(plage);
    }
    return "selection";
  }
}

export function CarteFilaments({
  catalogue,
  lignes,
  noms,
  teinteBase,
  corriger,
  choisirLaBase,
  filamentsDuJeu,
  ams,
}: {
  catalogue: CatalogueVu;
  /** Une ligne par couleur trouvee : la figurine de gauche, puis celle de droite. */
  lignes: readonly LigneFilament[];
  /** Les noms tels qu'on les montre : celui qui est tape, sinon « la figurine de gauche ». */
  noms: Record<Colonne, string>;
  /** La teinte des Bookends et des Socles. */
  teinteBase: TeinteBambu | null;
  corriger: (colonne: Colonne, couleur: string, cle: string) => void;
  choisirLaBase: (cle: string) => void;
  /** Les filaments du jeu, dans l'ordre de l'assemblage : la base d'abord, sans doublon. */
  filamentsDuJeu: readonly TeinteBambu[];
  /** Les figurines qui depassent les 4 filaments d'un AMS, et le compte de chaque colonne. */
  ams: { colonnes: readonly Colonne[]; nombres: Record<Colonne, number> };
}) {
  const id = useId();
  const titre = useRef<HTMLHeadingElement>(null);
  const zoneBom = useRef<HTMLParagraphElement>(null);
  const [copie, setCopie] = useState<Copie>(null);
  const texteBom = bom(filamentsDuJeu);
  const habituelles = catalogue.etat === "pret" ? catalogue.catalogue.habituelles : [];

  return (
    <section aria-labelledby={`${id}-titre`} className={CARTE}>
      {/* Focalisable : Reessayer disparait avec l'erreur qui le portait, le focus va au titre de la carte. */}
      <h2
        className="rounded-sm text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        id={`${id}-titre`}
        ref={titre}
        tabIndex={-1}
      >
        Filaments du jeu
      </h2>
      {/* Sans catalogue, la carte n'a rien a corriger : l'attente ou l'erreur prend la place de tout. */}
      {catalogue.etat === "pret" ? (
        <p className="mt-1 mb-2.5 text-[13px] text-muted-foreground">
          Chaque couleur trouvée est ramenée au PLA Bambu le plus proche (Basic et Matte) ; à
          distance presque égale, tes habituelles passent devant. Change-la ici : rien n'est
          recalculé, et tes choix sont retenus dans ce navigateur.
        </p>
      ) : null}

      {catalogue.etat === "attente" ? (
        <p className="text-sm text-muted-foreground" role="status">
          Les teintes Bambu se chargent…
        </p>
      ) : null}

      {catalogue.etat === "erreur" ? (
        <div className={cn(ALERTE.grise, "flex items-center justify-between gap-3")} role="alert">
          <span className="min-w-0 flex-1">
            Les teintes Bambu ne sont pas disponibles sur le serveur pour l'instant.
          </span>
          <Button
            className="min-h-11 shrink-0 md:min-h-8"
            onClick={() => {
              catalogue.reessayer();
              titre.current?.focus();
            }}
            size="sm"
            variant="outline"
          >
            Réessayer
          </Button>
        </div>
      ) : null}

      {catalogue.etat === "pret" && teinteBase ? (
        <>
          {lignes.length === 0 ? (
            <p className="mb-2 text-sm text-muted-foreground">
              Les couleurs trouvées apparaissent ici dès que les images sont rendues.
            </p>
          ) : (
            <div
              aria-hidden="true"
              className={cn(
                RANG,
                "hidden pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground xl:grid",
              )}
            >
              <span>Trouvée</span>
              <span>Figurine</span>
              <span />
              <span>PLA Bambu</span>
              <span />
            </div>
          )}
          <ul className="flex flex-col">
            {lignes.map((ligne) => {
              const note = noteDeLaLigne(ligne, lignes, habituelles);
              const couleur = libelleDeLaCouleur(ligne.couleur);
              return (
                <li
                  className={cn(RANG, "border-t border-border py-2 text-sm")}
                  key={`${ligne.colonne}:${ligne.couleur}`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Pastille hexa={ligne.hexa} />
                    <span className="truncate">{couleur}</span>
                  </span>
                  <span className="min-w-0 justify-self-end truncate text-[13px] text-muted-foreground xl:justify-self-start">
                    {noms[ligne.colonne]}
                  </span>
                  <span aria-hidden="true" className="hidden text-muted-foreground xl:block">
                    →
                  </span>
                  <SelecteurDeTeinte
                    catalogue={catalogue.catalogue}
                    changer={(cle) => corriger(ligne.colonne, ligne.couleur, cle)}
                    className="col-span-2 xl:col-span-1"
                    etiquette={`Teinte de ${couleur}, figurine de ${ligne.colonne}`}
                    valeur={ligne.choisie}
                  />
                  {note ? (
                    <p
                      className="col-span-2 text-[12.5px] text-muted-foreground xl:col-span-1 xl:text-[13px]"
                      data-note=""
                    >
                      {note}
                    </p>
                  ) : null}
                </li>
              );
            })}
            <li className={cn(RANG, "border-t border-border py-2 text-sm")}>
              <strong className="col-span-2 font-semibold xl:col-span-2">Bookends et Socles</strong>
              <span aria-hidden="true" className="hidden text-muted-foreground xl:block">
                →
              </span>
              <SelecteurDeTeinte
                catalogue={catalogue.catalogue}
                changer={choisirLaBase}
                className="col-span-2 xl:col-span-1"
                etiquette="Teinte des Bookends et des Socles"
                valeur={teinteBase}
              />
            </li>
          </ul>

          {ams.colonnes.length > 0 ? (
            <p className={cn(ALERTE.jaune, "mt-3")} role="status">
              {texteDeLAlerteAms(ams.colonnes, noms, ams.nombres)}
            </p>
          ) : null}

          {lignes.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="min-w-0 text-[13px] text-muted-foreground" ref={zoneBom}>
                Pour la BOM de MakerWorld : {texteBom}
              </p>
              <button
                className={cn(BOUTON_SECONDAIRE, "min-h-11 px-3 text-[13px] md:min-h-8")}
                onClick={() => void copier(texteBom, zoneBom.current).then(setCopie)}
                type="button"
              >
                Copier
              </button>
              <span className="text-[13px] text-muted-foreground" role="status">
                {copie === "copie" ? "Copiée." : null}
                {copie === "selection" ? "Sélectionnée : Ctrl+C pour la copier." : null}
              </span>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
